import { useAppDispatch, useAppSelector } from "@/config/store";
import { IEdoQuestion } from "@/shared/model/edo-question.model";
import { AuthenticationState } from "@/shared/reducers/authentication";
import { getQuestionsByAccount } from "@/shared/reducers/entities/edo-question.reducer";
import { EntityState } from "@/shared/reducers/reducer.utils";
import { ITEMS_PER_PAGE } from "@/shared/util/pagination.constants";
import { getQuestionType } from "@/shared/util/question-utils";
import {
  Card,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Pagination,
} from "@/components/ui";
import { Loader2, Eye } from "lucide-react";
import { useEffect, useState } from "react";
import { IPaginationBaseState, Translate } from "react-jhipster";
import SearchField from "../SearchField/SearchField";
import { cn } from "@/shared/util/cn";

interface SelectQuestionsProps {
  disabledQuestions?: IEdoQuestion[];
  selectedQuestions: IEdoQuestion[];
  setSelectedQuestions: (questions: IEdoQuestion[]) => void;
}

const SelectQuestions = (props: SelectQuestionsProps) => {
  const dispatch = useAppDispatch();
  const { account } = useAppSelector<AuthenticationState>(
    (state) => state.authentication
  );
  const {
    entities: edoQuestionList,
    updateSuccess,
    loading,
    totalItems,
  } = useAppSelector<EntityState<IEdoQuestion>>((state) => state.edoQuestion);
  const [paginationState, setPaginationState] = useState<IPaginationBaseState>({
    activePage: 1,
    itemsPerPage: ITEMS_PER_PAGE,
    sort: "updatedAt",
    order: "DESC",
  });
  const [searchValue, setSearchValue] = useState<string>("");

  const { selectedQuestions, disabledQuestions, setSelectedQuestions } = props;

  const handleGetEdoQuestionList = () => {
    const params = new URLSearchParams();
    params.append("filter.author.id", "$eq:" + account.id);
    if (searchValue) params.append("search", searchValue);
    dispatch(
      getQuestionsByAccount({
        page: paginationState.activePage,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
        filters: params.toString(),
      })
    );
  };

  useEffect(() => {
    handleGetEdoQuestionList();
  }, [
    searchValue,
    paginationState.activePage,
    paginationState.order,
    paginationState.sort,
    paginationState.itemsPerPage,
  ]);

  useEffect(() => {
    if (updateSuccess) {
      handleGetEdoQuestionList();
    }
  }, [updateSuccess]);

  const handlePagination = (currentPage: number) =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleRowsPerPage = (rowsPerPage: number) =>
    setPaginationState({
      ...paginationState,
      itemsPerPage: rowsPerPage,
      activePage: 1,
    });

  const toggleQuestion = (question: IEdoQuestion) => {
    const idx = selectedQuestions.findIndex((el) => el.id === question.id);
    if (idx === -1) {
      setSelectedQuestions([...selectedQuestions, question]);
    } else {
      const next = [...selectedQuestions];
      next.splice(idx, 1);
      setSelectedQuestions(next);
    }
  };

  const isDisabled = (q: IEdoQuestion) =>
    Boolean(disabledQuestions?.find((dq) => dq.id === q.id));
  const isSelected = (q: IEdoQuestion) =>
    selectedQuestions.findIndex((el) => el.id === q.id) !== -1;

  return (
    <Card className="surface overflow-hidden rounded-2xl">
      <div className="border-b border-[var(--color-mockup-border)] bg-[var(--color-surface-secondary)]/50 px-4 py-3 dark:border-[var(--color-mockup-border)] dark:bg-[var(--color-surface-secondary)]/30">
        <div className="flex flex-1 flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-heading font-semibold text-[var(--color-mockup-text)]">
            Domande
          </h2>
          <div className="min-w-0 flex-1 sm:max-w-xs">
            <SearchField
              placeholder="Cerca una domanda"
              value={searchValue}
              setSearchValue={setSearchValue}
            />
          </div>
        </div>
      </div>
      {loading && (
        <div className="flex justify-center py-2">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
        </div>
      )}
      {edoQuestionList.length ? (
        <List className="divide-y">
          {edoQuestionList.map((question) => {
            const disabled = isDisabled(question);
            const selected = isSelected(question) || disabled;
            return (
              <ListItem
                key={question.id}
                className={cn(
                  "flex cursor-pointer items-center gap-3 py-3 transition-colors hover:bg-primary/5",
                  selected && "bg-[var(--color-surface-secondary)]/50",
                  disabled && "cursor-not-allowed opacity-60"
                )}
                onClick={() => !disabled && toggleQuestion(question)}
              >
                <ListItemIcon className="pt-0.5">
                  <input
                    type="checkbox"
                    checked={selected}
                    disabled={disabled}
                    readOnly
                    className="h-4 w-4 rounded-sm border-[var(--color-mockup-border)] text-primary focus:ring-primary"
                    onChange={() => {}}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={question.description}
                  secondary={getQuestionType(question.type, true)}
                  className="min-w-0 truncate"
                />
                <ListItemSecondaryAction>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      // viewQuestion(question);
                    }}
                    aria-label="Visualizza domanda"
                    size="sm"
                  >
                    <Eye className="h-4 w-4" />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            );
          })}
        </List>
      ) : (
        <div className="p-4 text-center">
          <p className="text-body text-[var(--color-mockup-text-mid)]">
            <Translate contentKey="edocendoApp.edoQuestion.home.notFound" />
          </p>
        </div>
      )}
      <Pagination
        count={totalItems}
        page={paginationState.activePage - 1}
        rowsPerPage={paginationState.itemsPerPage}
        onPageChange={(p) => handlePagination(p + 1)}
        onRowsPerPageChange={handleRowsPerPage}
        labelRowsPerPage="Righe per pagina:"
        labelDisplayedRows={({ from, to, count }) =>
          `${from}-${to} di ${count !== -1 ? count : "more than" + to}`
        }
      />
    </Card>
  );
};

export default SelectQuestions;
